// Single shared entry point for every Google Places Autocomplete field in
// the app (checkout's billing/delivery fields, and the address-book "Find
// Your Address" search box).
//
// This used to be two separate files, each loading its own <script src=...>
// tag with a different callback= name (checkout_autocomplete.js /
// address_search.js). Turbolinks keeps `window.google` alive across page
// transitions, but a *different* script URL on the next page still gets
// re-inserted into <head> - so visiting the Addresses page and then Checkout
// in the same session loaded the Maps API twice, which Google's own loader
// flags as broken ("included the Google Maps JavaScript API multiple times")
// and left Autocomplete in an inconsistent state on whichever page loaded it
// second.
//
// Fix: every page uses the exact same script URL (see
// shared/_google_maps_script), so Turbolinks recognizes it's already loaded
// and never inserts it twice. Attaching happens once google.maps.places is
// genuinely ready - which, under the script's loading=async mode, is *not*
// the same moment as the <script> tag's own "load" event: the base loader
// fires "load" as soon as it exists, then fetches the Places library itself
// as separate, later requests. Watching for "load" and checking
// google.maps.places right then (what this used to do) loses that race
// almost every time - it looked broken with no error, since nothing ever
// retried afterwards. google.maps.importLibrary('places') is Google's own
// async-aware way to ask "tell me once this is really ready", and is used
// below instead. This is also retried on every turbolinks:load using
// whatever's already on window, for when the script was already loaded on
// an earlier page - each attach step is a no-op if its field isn't present
// on the current page, and idempotent if the field was already wired up.

function fillAddressField(id, value) {
  var el = document.getElementById(id)
  if (el && value) el.value = value
}

function attachIfPresent(inputId, onPlaceChanged) {
  var input = document.getElementById(inputId)
  if (!input || !window.google || !google.maps || !google.maps.places) return
  if (input.dataset.placesAttached) return
  input.dataset.placesAttached = 'true'

  var autocomplete = new google.maps.places.Autocomplete(input, { types: ['address'] })
  autocomplete.addListener('place_changed', function () {
    var place = autocomplete.getPlace()
    if (!place.address_components) return

    var components = {}
    place.address_components.forEach(function (component) {
      components[component.types[0]] = component
    })

    onPlaceChanged(input, components)
  })
}

// Checkout's billing/delivery sections each have their own dedicated
// "Find Your Address" search box (separate from the real Address Line 1
// field, same pattern as the address book) - see checkout/new.html.erb.
// Its data-record-prefix ("order" or "subscription", depending on one-time
// vs. weekly box) and data-address-prefix ("billing" or "delivery") say
// which real fields a selected place should fill in.
function attachCheckoutSearchBox(searchInput) {
  var recordPrefix = searchInput.dataset.recordPrefix
  var addressPrefix = searchInput.dataset.addressPrefix

  attachIfPresent(searchInput.id, function (input, components) {
    var streetNumber = components.street_number ? components.street_number.long_name : ''
    var route = components.route ? components.route.long_name : ''
    fillAddressField(recordPrefix + '_' + addressPrefix + '_address_line1', (streetNumber + ' ' + route).trim())

    var city = components.locality || components.postal_town
    fillAddressField(recordPrefix + '_' + addressPrefix + '_city', city && city.long_name)
    fillAddressField(recordPrefix + '_' + addressPrefix + '_state', components.administrative_area_level_1 && components.administrative_area_level_1.short_name)
    fillAddressField(recordPrefix + '_' + addressPrefix + '_zip', components.postal_code && components.postal_code.long_name)
    fillAddressField(recordPrefix + '_' + addressPrefix + '_country', components.country && components.country.long_name)
  })
}

function attachCheckoutSearchBoxes() {
  document.querySelectorAll('.js-places-search').forEach(attachCheckoutSearchBox)
}

function attachAddressBookSearch() {
  attachIfPresent('address_search', function (input, components) {
    var streetNumber = components.street_number ? components.street_number.long_name : ''
    var route = components.route ? components.route.long_name : ''
    fillAddressField('address_address_line1', (streetNumber + ' ' + route).trim())

    var city = components.locality || components.postal_town
    fillAddressField('address_city', city && city.long_name)
    fillAddressField('address_state', components.administrative_area_level_1 && components.administrative_area_level_1.short_name)
    fillAddressField('address_zip', components.postal_code && components.postal_code.long_name)
  })
}

function attachAllPlacesFields() {
  attachCheckoutSearchBoxes()
  attachAddressBookSearch()
}

// Calls callback() once google.maps.places is genuinely ready. Polls rather
// than waiting on the <script> tag's own "load" event or the
// importLibrary() promise - both turned out unreliable in practice here (the
// "load" event in particular: under this script's loading=async mode, it
// can fire before google.maps.places (a separate, later request) actually
// exists, and - empirically, on this exact tag - listeners attached to it
// before that "load" event were observed to never run at all once it did
// eventually fire, for reasons that didn't reduce to anything reproducible
// in isolation). Polling has no dependency on any of that: it just asks
// "is it ready yet?" on a plain interval until it is, or until it's clearly
// never going to be.
function whenPlacesReady(callback) {
  var attempts = 0
  var maxAttempts = 100 // 100 * 200ms = 20s

  var poll = setInterval(function () {
    attempts += 1

    if (window.google && google.maps && google.maps.places) {
      clearInterval(poll)
      callback()
      return
    }

    if (attempts === 1 && !document.getElementById('google-maps-script')) {
      // Only happens when ENV['GOOGLE_MAPS_API_KEY'] is blank on this server
      // - shared/_google_maps_script never renders the <script> tag at all,
      // so this was never going to become ready. Check the deployed env vars.
      console.warn('[places_autocomplete] No #google-maps-script tag on this page - GOOGLE_MAPS_API_KEY is likely unset on this server.')
      clearInterval(poll)
      return
    }

    if (attempts >= maxAttempts) {
      // Loaded (or the tag wouldn't exist) but never became ready within
      // 20s - likely an invalid/restricted API key, Places API not enabled,
      // or billing not set up on that Google Cloud project. Check the
      // Network tab for the request to maps.googleapis.com/maps/api/js and
      // read its response body for the specific error.
      console.error('[places_autocomplete] Gave up waiting for google.maps.places after 20s.')
      clearInterval(poll)
    }
  }, 200)
}

document.addEventListener('turbolinks:load', function () {
  whenPlacesReady(attachAllPlacesFields)
})
